import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle, Skeleton } from '@insolech/ui';
import { Mail } from 'lucide-react';
import {
  adminQueries,
  lineProviderAssignmentQueries,
  type AdminProviderRow,
  type LocalChainStage,
  type OrderDetail,
} from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';
import { isCustomLine } from './productLabel';

const { LOCAL_CHAIN_STAGES, LOCAL_CHAIN_STAGE_LABELS } = lineProviderAssignmentQueries;
const { PROVIDER_TYPE_LABELS } = adminQueries;

const STAGE_TO_PROVIDER_TYPE: Record<LocalChainStage, 'local_designer' | 'local_printer' | 'local_workshop'> = {
  designer: 'local_designer',
  printer: 'local_printer',
  workshop: 'local_workshop',
};

interface Props {
  order: OrderDetail;
  /** Admin scope unlocks editing; podiatrist scope is read-only. */
  isAdmin: boolean;
}

/**
 * Production routing card. Renders for custom-made orders only. Shows the
 * production path (EU end-to-end vs three-stage local chain), the provider
 * assignment(s), and a "Send email" mailto shortcut per provider so the
 * admin can hand the order off without leaving the page.
 *
 * The path is auto-derived from solution_subtype by a DB trigger at insert,
 * and the admin can override here. For EU orders the assignment uses
 * order_lines.assigned_provider_id; for LOCAL orders it uses three rows in
 * line_provider_assignments (one per stage).
 */
export function OrderProductionCard({ order, isAdmin }: Props) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const primaryLine = order.lines[0];
  // Hooks must run unconditionally on every render. Guard the values
  // they need with sensible fallbacks; gate the render at the bottom.
  const lineId = primaryLine?.id ?? '';
  const path = (primaryLine?.production_path ?? 'eu') as 'eu' | 'local';
  const isCustom = !!primaryLine && isCustomLine(primaryLine.line_type);

  const providersQuery = useQuery({
    ...adminQueries.adminProviderListQuery(supabase, { activeFilter: 'active' }),
    enabled: isCustom,
  });
  const assignmentsQuery = useQuery({
    ...lineProviderAssignmentQueries.lineProviderAssignmentsQuery(supabase, lineId),
    enabled: isCustom && path === 'local' && lineId !== '',
  });

  const invalidateAll = async () => {
    await queryClient.invalidateQueries({ queryKey: ['orders', 'detail', order.id] });
    await queryClient.invalidateQueries({
      queryKey: lineProviderAssignmentQueries.lineProviderAssignmentKeys.forLine(lineId),
    });
  };

  const pathMutation = useMutation({
    mutationFn: async (next: 'eu' | 'local') => {
      // Two-step cascade: write the new path, then clear assignments on the
      // abandoned side so the order can't show stale provider data after a
      // toggle. Not transactional (no RPC for v1) but recoverable -- the
      // path is the authoritative flag and the cleanup retries on next save.
      const { error: pathErr } = await supabase
        .from('order_lines')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ production_path: next } as any)
        .eq('id', lineId);
      if (pathErr) throw pathErr;
      if (next === 'local') {
        // Switched to local chain: drop the EU single-provider assignment.
        const { error: clearEuErr } = await supabase
          .from('order_lines')
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .update({ assigned_provider_id: null } as any)
          .eq('id', lineId);
        if (clearEuErr) throw clearEuErr;
      } else {
        // Switched to EU: drop every stage row for this line.
        const { error: clearStagesErr } = await supabase
          .from('line_provider_assignments')
          .delete()
          .eq('order_line_id', lineId);
        if (clearStagesErr) throw clearStagesErr;
      }
    },
    onSuccess: invalidateAll,
  });

  const euProviderMutation = useMutation({
    mutationFn: async (providerId: string | null) => {
      const { error } = await supabase
        .from('order_lines')
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .update({ assigned_provider_id: providerId } as any)
        .eq('id', lineId);
      if (error) throw error;
    },
    onSuccess: invalidateAll,
  });

  const stageMutation = useMutation({
    mutationFn: async (args: { stage: LocalChainStage; providerId: string | null }) => {
      if (!user?.id) throw new Error('You must be signed in.');
      await lineProviderAssignmentQueries.setLineStageProvider(supabase, {
        orderLineId: lineId,
        stage: args.stage,
        providerId: args.providerId,
        assignedBy: user.id,
      });
    },
    onSuccess: invalidateAll,
  });

  // Hooks are all called above. Now we can safely gate the render.
  if (!primaryLine || !isCustom) return null;

  const providers: AdminProviderRow[] = providersQuery.data ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Production</CardTitle>
          <span
            className={[
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
              path === 'eu'
                ? 'bg-brand-50 text-brand-800 border border-brand-200'
                : 'bg-clay-50 text-clay-800 border border-clay-200',
            ].join(' ')}
          >
            {path === 'eu' ? 'EU end-to-end' : 'Local chain'}
          </span>
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        {isAdmin ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-text-secondary">Route:</span>
            <button
              type="button"
              className={[
                'h-8 px-3 rounded-md border text-xs font-medium transition-colors',
                path === 'eu'
                  ? 'bg-brand-700 text-white border-brand-700'
                  : 'bg-surface-elevated text-text-secondary border-border-default hover:bg-sand-50',
              ].join(' ')}
              onClick={() => pathMutation.mutate('eu')}
              disabled={path === 'eu' || pathMutation.isPending}
            >
              EU end-to-end
            </button>
            <button
              type="button"
              className={[
                'h-8 px-3 rounded-md border text-xs font-medium transition-colors',
                path === 'local'
                  ? 'bg-brand-700 text-white border-brand-700'
                  : 'bg-surface-elevated text-text-secondary border-border-default hover:bg-sand-50',
              ].join(' ')}
              onClick={() => pathMutation.mutate('local')}
              disabled={path === 'local' || pathMutation.isPending}
            >
              Local chain
            </button>
          </div>
        ) : null}

        {providersQuery.isLoading ? <Skeleton className="h-10 w-full" /> : null}

        {path === 'eu' ? (
          <EuPathAssignment
            order={order}
            providers={providers.filter((p) => p.provider_type === 'eu_end_to_end')}
            currentProviderId={primaryLine.assigned_provider?.id ?? null}
            currentProviderName={primaryLine.assigned_provider?.name ?? null}
            isAdmin={isAdmin}
            onChange={(id) => euProviderMutation.mutate(id)}
            pending={euProviderMutation.isPending}
          />
        ) : (
          <LocalChainAssignments
            order={order}
            providers={providers}
            assignments={assignmentsQuery.data ?? []}
            loading={assignmentsQuery.isLoading}
            isAdmin={isAdmin}
            onChange={(stage, id) => stageMutation.mutate({ stage, providerId: id })}
            pending={stageMutation.isPending}
          />
        )}

        <p className="text-xs text-text-muted pt-2 border-t border-border-subtle">
          The route is auto-set from the product type at submit
          (PA12 / TPU &rarr; Local, others &rarr; EU). Admin can override here.
        </p>
      </CardBody>
    </Card>
  );
}

function EuPathAssignment({
  order,
  providers,
  currentProviderId,
  currentProviderName,
  isAdmin,
  onChange,
  pending,
}: {
  order: OrderDetail;
  providers: AdminProviderRow[];
  currentProviderId: string | null;
  currentProviderName: string | null;
  isAdmin: boolean;
  onChange: (id: string | null) => void;
  pending: boolean;
}) {
  const current = providers.find((p) => p.id === currentProviderId);
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        EU end-to-end provider
      </div>
      {isAdmin ? (
        <select
          value={currentProviderId ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={pending}
          className="w-full h-10 rounded-md border border-border-default bg-surface-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        >
          <option value="">— Unassigned —</option>
          {providers.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      ) : (
        <p className="text-sm text-text-primary">
          {currentProviderName ?? <span className="text-text-muted">Unassigned</span>}
        </p>
      )}
      {current?.contact_email ? (
        <MailToProviderButton
          order={order}
          provider={current}
          subjectSuffix="production"
        />
      ) : null}
    </div>
  );
}

function LocalChainAssignments({
  order,
  providers,
  assignments,
  loading,
  isAdmin,
  onChange,
  pending,
}: {
  order: OrderDetail;
  providers: AdminProviderRow[];
  assignments: {
    stage: LocalChainStage;
    provider: { id: string; name: string; contact_email: string | null } | null;
  }[];
  loading: boolean;
  isAdmin: boolean;
  onChange: (stage: LocalChainStage, id: string | null) => void;
  pending: boolean;
}) {
  if (loading) {
    return <Skeleton className="h-32 w-full" />;
  }
  return (
    <div className="space-y-3">
      {LOCAL_CHAIN_STAGES.map((stage) => {
        const expectedType = STAGE_TO_PROVIDER_TYPE[stage];
        const stageProviders = providers.filter((p) => p.provider_type === expectedType);
        const assignment = assignments.find((a) => a.stage === stage);
        const current = assignment?.provider ?? null;
        return (
          <div key={stage} className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
              {LOCAL_CHAIN_STAGE_LABELS[stage]}
            </div>
            {isAdmin ? (
              <select
                value={current?.id ?? ''}
                onChange={(e) => onChange(stage, e.target.value || null)}
                disabled={pending}
                className="w-full h-10 rounded-md border border-border-default bg-surface-elevated px-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              >
                <option value="">— Unassigned —</option>
                {stageProviders.length === 0 ? (
                  <option value="" disabled>
                    (no {PROVIDER_TYPE_LABELS[expectedType]} providers configured)
                  </option>
                ) : null}
                {stageProviders.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-sm text-text-primary">
                {current?.name ?? <span className="text-text-muted">Unassigned</span>}
              </p>
            )}
            {current ? (
              <StageMailToButton
                order={order}
                providerId={current.id}
                providerName={current.name}
                providerEmail={current.contact_email}
                stage={stage}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function MailToProviderButton({
  order,
  provider,
  subjectSuffix,
}: {
  order: OrderDetail;
  provider: { name: string; contact_email: string | null };
  subjectSuffix: string;
}) {
  if (!provider.contact_email) {
    return (
      <p className="text-xs text-text-muted">
        No contact email on file for {provider.name}.
      </p>
    );
  }
  const patientLine = order.patient?.full_name
    ? `Patient: ${order.patient.full_name}\n`
    : '';
  const subject = `INSOLECH order ${order.order_number} — ${subjectSuffix}`;
  const body =
    `Hi ${provider.name},\n\n` +
    `Please find attached the production package for order ${order.order_number}.\n\n` +
    patientLine +
    `Order detail: open this order on the INSOLECH admin to download the attached files (prescription, scans, STL).\n\n` +
    `Thanks,\nINSOLECH`;
  const href = `mailto:${provider.contact_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return (
    <a
      href={href}
      className="inline-flex items-center gap-1.5 text-xs text-brand-700 hover:underline"
    >
      <Mail className="h-3.5 w-3.5" />
      Email {provider.name}
    </a>
  );
}

function StageMailToButton({
  order,
  providerName,
  providerEmail,
  stage,
}: {
  order: OrderDetail;
  providerId: string;
  providerName: string;
  providerEmail: string | null;
  stage: LocalChainStage;
}) {
  return (
    <MailToProviderButton
      order={order}
      provider={{ name: providerName, contact_email: providerEmail }}
      subjectSuffix={`${stage} stage`}
    />
  );
}
